import type React from "react";
import { useEffect } from "react";
import { Link } from "react-router";
import { Helmet } from "react-helmet-async";
import { APP_ROUTES } from "@/pages/routes";

const ResourcesPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="flex min-h-screen w-full flex-col bg-white dark:bg-gray-950">
      <Helmet>
        <title>Resources — MycenAI Clinical Prompt Library for PAT</title>
        <meta name="description" content="Free clinical documentation resources for psychedelic-assisted therapy practitioners. Download the PAT Clinical Prompt Library with evidence-based templates for intake, preparation, dosing, integration, and discharge." />
        <link rel="canonical" href="https://mycenai.org/resources/" />
        <meta property="og:title" content="Resources — MycenAI Clinical Prompt Library for PAT" />
        <meta property="og:description" content="Free clinical documentation resources for psychedelic-assisted therapy practitioners." />
        <meta property="og:url" content="https://mycenai.org/resources/" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Header */}
      <header className="border-gray-200 border-b bg-white/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/80">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to={APP_ROUTES.SPLASH}>
            <span className="font-bold text-gray-900 text-xl dark:text-white">
              Mycen<span className="text-violet-600 dark:text-violet-400">AI</span>
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to={APP_ROUTES.BLOG} className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400">Blog</Link>
            <Link to={APP_ROUTES.RESOURCES} className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400">Resources</Link>
            <Link to={APP_ROUTES.ABOUT} className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400">About</Link>
            <a href="https://confideai.ai/resources" className="hidden font-medium text-gray-500 text-sm transition hover:text-blue-600 sm:inline dark:text-gray-400 dark:hover:text-blue-400">ConfideAI Resources →</a>
            <a href="https://confideai.ai/welcome/mycenai" className="rounded-lg bg-violet-600 px-6 py-2.5 font-semibold text-sm text-white shadow-sm transition hover:bg-violet-700">Get Started</a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="mx-auto w-full max-w-4xl px-4 py-12 sm:px-6">
          <Link to={APP_ROUTES.SPLASH} className="mb-8 inline-block text-gray-500 text-sm transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">&larr; Home</Link>

          <h1 className="mb-4 font-bold text-3xl text-gray-900 leading-tight sm:text-4xl dark:text-white">
            Clinical Resources
          </h1>
          <p className="mb-12 max-w-2xl text-gray-600 text-lg dark:text-gray-400">
            Free, evidence-based documentation resources for psychedelic-assisted therapy practitioners. Download, adapt, and use in your clinical practice.
          </p>

          {/* Resource Cards */}
          <div className="grid gap-8">
            {/* PAT Clinical Prompt Library */}
            <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
                  <svg className="h-7 w-7 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h2 className="mb-2 font-bold text-gray-900 text-xl dark:text-white">
                    Clinical Prompt Library for Psychedelic-Assisted Therapy
                  </h2>
                  <p className="mb-4 text-gray-600 text-sm leading-relaxed dark:text-gray-400">
                    43-page reference document with evidence-based AI prompt templates covering the full PAT treatment arc. Includes templates for intake and assessment, preparation, dosing/ceremony sessions, integration, treatment planning, progress notes, discharge, supervision, research tracking, crisis documentation, and group therapy.
                  </p>
                  <div className="mb-5 flex flex-wrap gap-2">
                    <span className="rounded-full bg-violet-50 px-3 py-1 text-violet-700 text-xs dark:bg-violet-900/20 dark:text-violet-300">11 sections</span>
                    <span className="rounded-full bg-violet-50 px-3 py-1 text-violet-700 text-xs dark:bg-violet-900/20 dark:text-violet-300">26 templates</span>
                    <span className="rounded-full bg-violet-50 px-3 py-1 text-violet-700 text-xs dark:bg-violet-900/20 dark:text-violet-300">APA referenced</span>
                    <span className="rounded-full bg-violet-50 px-3 py-1 text-violet-700 text-xs dark:bg-violet-900/20 dark:text-violet-300">CC BY-NC 4.0</span>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <a
                      href="/resources/PAT_Clinical_Prompt_Library_MycenAI.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 font-semibold text-sm text-white shadow-sm transition hover:bg-violet-700"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                      Download PDF
                    </a>
                    <span className="text-gray-400 text-xs dark:text-gray-500">Version 1.0 &middot; February 2026 &middot; 43 pages</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Community Forum */}
          <div className="mt-16 rounded-xl border border-gray-200 bg-gray-50 p-8 text-center dark:border-gray-800 dark:bg-gray-900">
            <h2 className="mb-3 font-bold text-gray-900 text-xl dark:text-white">Community Forum</h2>
            <p className="mx-auto mb-6 max-w-xl text-gray-600 text-sm dark:text-gray-400">
              Join our community of mental health professionals to share prompts, discuss best practices, and exchange resources.
            </p>
            <span className="inline-block rounded-lg border border-gray-200 px-6 py-2.5 font-medium text-gray-400 text-sm dark:border-gray-700 dark:text-gray-500">
              Coming Soon
            </span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-gray-200 border-t bg-gray-50 px-6 py-8 dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center">
          <p className="text-gray-500 text-sm dark:text-gray-400">&copy; 2026 MycenAI. All rights reserved.</p>
          <div className="flex gap-6 text-gray-500 text-sm dark:text-gray-400">
            <Link to={APP_ROUTES.FAQ} className="transition hover:text-violet-600 dark:hover:text-violet-400">FAQ</Link>
            <Link to={APP_ROUTES.PRIVACY} className="transition hover:text-violet-600 dark:hover:text-violet-400">Privacy</Link>
            <Link to={APP_ROUTES.TERMS} className="transition hover:text-violet-600 dark:hover:text-violet-400">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResourcesPage;
